import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder } from '@angular/forms';
import { Observable } from 'rxjs';
import { withLatestFrom, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { Api } from '../../../../../../../shared/constants/api';
import { UpsertTenantWorkspaceComponent } from '../upsert-tenant-workspace.component';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { TranslateService } from '../../../../../translate/translate.service';
import { WorkspaceService } from '../../../../../services/workspace.service';
import { NotificationService } from '../../../../../services/notification.service';
import { $workspace } from '../../../../../../../shared/constants/workspace';
import { IWorkspace } from '../../../../../../../shared/interfaces/workspace.interface';

@Component({
  selector: 'app-edit-tenant-workspace',
  templateUrl: '../upsert-tenant-workspace.component.html',
  standalone: false,
})
export class EditTenantWorkspaceComponent extends UpsertTenantWorkspaceComponent implements OnInit {
  public workspaceId: string;

  public constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    formBuilder: UntypedFormBuilder,
    loadErrorHandler: LoadErrorHandler,
    translateService: TranslateService,
    workspaceService: WorkspaceService,
    notificationService: NotificationService,
  ) {
    super(router, activatedRoute, formBuilder, loadErrorHandler, translateService, workspaceService, notificationService);
  }

  private onGetWorkspaceSuccess(workspace: IWorkspace): void {
    this.form.setValue({
      [$workspace.name]: workspace[$workspace.name],
      [$workspace.description]: workspace[$workspace.description],
    });
  }

  private async onGetWorkspaceFailure(response: HttpErrorResponse): Promise<void> {
    this.loadErrorHandler.handleError('error_while_getting_admin_workspace', response, EditTenantWorkspaceComponent.name);
    await this.router.navigate([this.tenantRoutePath, this.tenantId]);
  }

  protected sendRequest$(): Observable<IWorkspace> {
    return this.submitForm$.pipe(
      withLatestFrom(this.form.valueChanges),
      switchMap(([_, formValue]: [void, Record<$workspace.name | $workspace.description, string>]) =>
        this.workspaceService.updateWorkspaceAsAdmin(this.workspaceId, {
          [$workspace.name]: formValue[$workspace.name],
          [$workspace.description]: formValue[$workspace.description],
        }),
      ),
    );
  }

  public get title(): string {
    return this.translateService.instant('edit_workspace');
  }

  public get submitButtonLabel(): string {
    return this.translateService.instant('action_update');
  }

  public get submissionSuccessMessage(): string {
    return this.translateService.instant('workspace_update_success');
  }

  public get submissionFailureMessage(): string {
    return this.translateService.instant('workspace_update_error');
  }

  public ngOnInit(): void {
    this.workspaceId = this.activatedRoute.snapshot.params[Api.workspaceId];
    super.ngOnInit();

    this.subscriptions.add(
      this.workspaceService.getWorkspace(this.workspaceId).subscribe({
        next: this.onGetWorkspaceSuccess.bind(this),
        error: this.onGetWorkspaceFailure.bind(this),
      }),
    );
  }
}
